import { ReactNode } from "react";
import { SidebarProvider } from "./Sidebar";
import { BlobBackground } from "./ui";
import { useBranding, generateBlobColors } from "@/lib/branding-context";
import { useAuth } from "@/lib/auth-context";
import { isVirtualReceptionist } from "@/lib/vr-api";
import { AgentChatBar } from "./AgentChatBar";

// ════════════════════════════════════════════════════════════════════════════
// Dashboard Layout
// ════════════════════════════════════════════════════════════════════════════
// Wraps dashboard pages with the sidebar and blob background.
// The SidebarProvider handles fluid content adjustment when sidebar expands.
// Uses branding colors from context to customize blob background.

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { branding } = useBranding();
  const { user, isLoading } = useAuth();

  // Virtual receptionists only get the /vr workspace — never tenant/admin pages
  if (!isLoading && isVirtualReceptionist(user)) {
    window.location.href = "/vr";
    return null;
  }

  // Generate blob colors from extracted logo colors
  const blobColors = branding?.extractedColors?.length
    ? generateBlobColors(branding.extractedColors)
    : undefined;

  return (
    <div className="min-h-screen">
      <BlobBackground colors={blobColors} />
      <SidebarProvider>
        <div className="relative z-10">
          {children}
        </div>
      </SidebarProvider>
      <AgentChatBar />
    </div>
  );
}

export default DashboardLayout;
